import { ReactElement } from 'react';

import { DocplannerService, DocplannerServiceQuery } from '@dreryk/egabinet-docplanner-frontend-api';

import { CqrsInfiniteQueryResult, useCqrsInfiniteQuery } from '@dreryk/command-query-react-common';
import { createTranslatedText } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';

import { medicalServicesMappingsTranslationKeys } from '../../../translations';

import { DocplannerServiceMappingCreateFormController } from './docplannerServiceMappingCreate.types';

const createDocplannerServicesSearchCreator = (excludedIds?: number[]) => (
   searchText: string | null
): CqrsInfiniteQueryResult<DocplannerService[]> => {
   // eslint-disable-next-line @typescript-eslint/require-await
   return useCqrsInfiniteQuery<DocplannerService>(
      () => {
         const query = new DocplannerServiceQuery();
         query.namePattern = TextPattern.anywhereIgnoreCase(searchText ?? undefined);
         query.excludedIds = Slot.emptyIfNull(excludedIds ?? null);
         return query;
      },
      { limit: 100 }
   );
};

const triggerLabelExtractor = (docplannerService: DocplannerService): string => docplannerService.externalName;

type DocplannerServiceSelectProps = {
   readonly register: DocplannerServiceMappingCreateFormController['register'];
   readonly excludedIds: number[] | undefined;
};

export const DocplannerServiceSelect = (props: DocplannerServiceSelectProps): ReactElement => {
   const useDocplannerServicesSearchCreator = createDocplannerServicesSearchCreator(props.excludedIds);
   return (
      <Select
         testId={'docplannerService'}
         triggerLabelExtractor={triggerLabelExtractor}
         controller={props.register('docplannerService', { required: true })}
         placeholder={medicalServicesMappingsTranslationKeys.chooseDocplannerService}
         label={medicalServicesMappingsTranslationKeys.docplannerService}
         searchInputEnabled
      >
         <Select.InfiniteSearchCreator<DocplannerService> searchCreator={useDocplannerServicesSearchCreator}>
            {({ pages }) => (
               <Select.List<DocplannerService> items={pages.flat()} keyExtractor={'externalId'}>
                  {(docplannerService) => (
                     <Select.Item<DocplannerService>
                        item={docplannerService}
                        testId={docplannerService.externalId}
                        label={createTranslatedText(docplannerService.externalName)}
                     />
                  )}
               </Select.List>
            )}
         </Select.InfiniteSearchCreator>
      </Select>
   );
};
